<template>
  <Teleport to="#cropper-container">
    <div
      v-if="selectedImage"
      class="fixed inset-0 bg-gray-600 bg-opacity-50 z-[99999] flex justify-center items-center"
    >
      <div
        class="bg-white dark:bg-black p-4 rounded-lg shadow-lg mx-auto size-auto flex flex-col space-y-4 justify-center items-center max-h-[90vh] max-w-[100svw]"
      >
        <!-- Cropper Container -->
        <div class="relative">
          <img
            ref="imageElement"
            :src="selectedImage"
            class="max-w-[95svw] h-auto max-h-[75vh]"
          />
        </div>
        <!-- Cropper Controls -->
        <div class="flex justify-end align-end items-center space-x-2">
          <button
            @click="cancelCrop"
            class="secondary-color text-white py-2 px-4 m-2 rounded cursor-pointer select-none"
          >
            Cancel
          </button>
          <button
            @click="cropImage"
            class="primary-color text-white font-bold py-2 px-4 m-2 rounded cursor-pointer select-none"
          >
            Upload
          </button>
        </div>
      </div>
    </div>
  </Teleport>
  <input
    type="file"
    ref="fileInput"
    @change="onSelectFile"
    accept="image/*"
    class="hidden"
  />
</template>

<script setup>
import {
  ref,
  watch,
  nextTick,
  onBeforeUnmount,
  computed,
  onMounted,
} from "vue";
import Cropper from "cropperjs";
import heic2any from "heic2any";
import "cropperjs/dist/cropper.css";

const props = defineProps({
  aspectRatio: {
    type: Number,
    default: null,
  },
  type: String,
  isGroup: Boolean,
});

const emit = defineEmits(["croppedImage"]);

const selectedImage = ref(null);
const fileInput = ref(null);

// Create container for modal if it doesn't exist
onMounted(() => {
  let container = document.getElementById("modal-container");
  if (!container) {
    container = document.createElement("div");
    container.id = "modal-container";
    document.body.appendChild(container);
  }
});

function openFileInput() {
  if (fileInput.value) {
    fileInput.value.click(); // Programmatically trigger the file input click event
  }
}

function cancelCrop() {
  try {
    if (cropper) {
      resetCropper();
    }
    selectedImage.value = null;

    // Only emit the type if it exists
    if (props.type) {
      emit("croppedImage", null, props.type);
    } else {
      // Just emit null for both parameters if type is not available
      emit("croppedImage", null, null);
    }
  } catch (e) {
    console.error("[ImageCropper] Error in cancelCrop:", e);
  }
}

// New method to directly set an image for cropping without using file input
function setImageForCropping(imageData) {
  try {
    selectedImage.value = imageData;
    nextTick(() => {
      initializeCropper();
    });
  } catch (e) {
    console.error("[ImageCropper] Error in setImageForCropping:", e);
  }
}

// Add a complete reset method that can be called externally
function resetCropper() {
  try {
    // Store URL for revocation before clearing selectedImage
    const urlToRevoke =
      selectedImage.value && selectedImage.value.startsWith("blob:")
        ? selectedImage.value
        : null;

    // First destroy the cropper if it exists
    if (cropper) {
      try {
        cropper.destroy();
      } catch (e) {
        console.error("[ImageCropper] Error destroying cropper:", e);
      }
      cropper = null;
    }

    // Clear the selected image to remove the teleported UI
    selectedImage.value = null;

    // Clean up any URL objects to prevent memory leaks (using saved URL)
    if (urlToRevoke) {
      try {
        URL.revokeObjectURL(urlToRevoke);
      } catch (e) {
        console.error("[ImageCropper] Error revoking URL:", e);
      }
    }

    // Clear the file input
    if (fileInput.value) {
      try {
        fileInput.value.value = "";
      } catch (e) {
        console.error("[ImageCropper] Error clearing file input:", e);
      }
    }
  } catch (e) {
    console.error("[ImageCropper] Error in resetCropper:", e);
  }
}

defineExpose({
  openFileInput,
  cancelCrop,
  setImageForCropping,
  fileInput,
  resetCropper,
});

const imageElement = ref(null);
let cropper = null;

const cropperOptions = computed(() => {
  const options = {
    viewMode: 1,
    dragMode: "move",
    autoCropArea: 1,
    restore: false,
    guides: true,
    center: true,
    highlight: true,
    cropBoxMovable: true,
    cropBoxResizable: true,
    toggleDragModeOnDblclick: false,
    ready: function () {
      if (props.type === "avatar") {
        // Set the crop box data to make it a square
        var containerData = this.cropper.getContainerData();
        var cropBoxSize = Math.min(containerData.width, containerData.height);
        this.cropper.setCropBoxData({
          left: (containerData.width - cropBoxSize) / 2,
          top: (containerData.height - cropBoxSize) / 2,
          width: cropBoxSize,
          height: cropBoxSize,
        });

        if (!props.isGroup) {
          // Apply styles to make the crop box appear circular
          var cropBoxElement = this.cropper.cropBox;
          cropBoxElement.style.borderRadius = "50%";
          cropBoxElement.style.opacity = "1";
          var faceElement =
            cropBoxElement.getElementsByClassName("cropper-face")[0];
          if (faceElement) {
            faceElement.style.borderRadius = "50%";
          }
        } else {
          // Apply styles to make the crop box appear circular
          var groupCropBoxElement = this.cropper.cropBox;
          groupCropBoxElement.style.borderRadius = "15%";
          groupCropBoxElement.style.opacity = "1";
          var groupFaceElement =
            groupCropBoxElement.getElementsByClassName("cropper-face")[0];
          if (groupFaceElement) {
            groupFaceElement.style.borderRadius = "15%";
          }
        }
      }
    },
  };

  // Only set aspectRatio if not freeform type
  if (props.type !== "freeform" && props.aspectRatio) {
    options.aspectRatio = props.aspectRatio;
  }

  return options;
});

async function onSelectFile(event) {
  try {
    if (!event.target || !event.target.files || !event.target.files[0]) {
      return;
    }

    const file = event.target.files[0];
    if (file && file.type.startsWith("image/")) {
      if (
        file.type === "image/heic" ||
        file.name.toLowerCase().endsWith(".heic")
      ) {
        try {
          const convertedBlob = await heic2any({
            blob: file,
            toType: "image/jpeg",
            quality: 0.9,
          });

          const convertedFile = new File(
            [convertedBlob],
            file.name.replace(/\.heic$/i, ".jpg"),
            {
              type: "image/jpeg",
            }
          );

          const reader = new FileReader();
          reader.onload = (e) => {
            selectedImage.value = e.target.result;
            initializeCropper();
          };
          reader.readAsDataURL(convertedFile);

          emit("croppedImage", convertedFile, props.type);
        } catch (error) {
          console.error("[ImageCropper] HEIC conversion failed:", error);
        }
      } else if (file.type === "image/gif") {
        // Handle GIF files differently (skip cropping)
        selectedImage.value = URL.createObjectURL(file);
        emit("croppedImage", file, props.type);
      } else {
        const reader = new FileReader();
        reader.onload = (e) => {
          selectedImage.value = e.target.result;
          initializeCropper();
        };
        reader.readAsDataURL(file);
      }
    }
  } catch (e) {
    console.error("[ImageCropper] Error in onSelectFile:", e);
  }
}

function initializeCropper() {
  try {
    nextTick(() => {
      if (!imageElement.value) {
        console.error("[ImageCropper] Image element not found");
        return;
      }

      if (cropper) {
        try {
          cropper.destroy();
        } catch (e) {
          console.error(
            "[ImageCropper] Error destroying cropper in initialize:",
            e
          );
        }
      }
      cropper = new Cropper(imageElement.value, cropperOptions.value);
    });
  } catch (e) {
    console.error("[ImageCropper] Error in initializeCropper:", e);
  }
}

function cropImage() {
  try {
    if (!cropper) {
      console.error("[ImageCropper] Cropper not initialized");
      return;
    }

    const canvas = cropper.getCroppedCanvas();
    canvas.toBlob((blob) => {
      emit("croppedImage", blob, props.type);
      // Close the cropper dialog after successful crop
      selectedImage.value = null;
    }, "image/jpeg");
  } catch (e) {
    console.error("[ImageCropper] Error in cropImage:", e);
  }
}

watch(
  () => [props.aspectRatio, props.type],
  () => {
    try {
      if (selectedImage.value) {
        initializeCropper();
      }
    } catch (e) {
      console.error("[ImageCropper] Error in watch handler:", e);
    }
  }
);

onBeforeUnmount(() => {
  try {
    // Use the reset method to ensure all resources are cleared
    resetCropper();

    // DO NOT remove the container - just ensure cropper is cleaned up
    // Other components may still need the container
  } catch (e) {
    console.error("[ImageCropper] Error in onBeforeUnmount:", e);
  }
});
</script>

<style scoped>
.image-container {
  /* Set a max height and width to fit the screen while allowing for some padding */
  max-width: 90vw;
  max-height: 80vh;
}

/* Ensure the cropper image is responsive and scales down on smaller screens */
.image-container img {
  display: block;
  /* Remove inline-block spacing */
  max-width: 100%;
  /* Max width is 100% of the container */
  max-height: 75vh;
  /* Max height is 75% of the viewport height */
  margin: auto;
  /* Center it horizontally */
}

.primary-color {
  background-color: var(--primary-color);
}

.secondary-color {
  background-color: var(--secondary-color);
}
</style>
